import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Dialog, DialogContent, TextField, Typography,
  DialogTitle, DialogActions, Stack, CircularProgress, Alert
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';

// Tus hooks, constantes e interfaces
import { useCompanyCore } from '../../../core/modules/company';
import { useGlobalCore } from '../../../core/modules/global';
import { CREATE_STORE_DIALOG } from '../../../core/constants/dialog';

interface FormData {
  name: string;
}

const schema = yup.object().shape({
  name: yup.string().required('Debes ingresar un nombre para la sucursal'),
});

function StoreDialog() {
  const { dialog, closeDialog, showSnackbar } = useGlobalCore();
  const { company, store, setStore, getStores, postStore, putStore } = useCompanyCore();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const isEditing = !!store;

  const { control, formState, handleSubmit, reset } = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors, isDirty } = formState;

  useEffect(() => {
    // Poblar el formulario si estamos en modo edición
    if (isEditing) {
      reset({ name: store.name });
    } else {
      reset({ name: '' }); // Limpiar para creación
    }
  }, [isEditing, store, reset]);

  const handleClose = () => {
    setApiError(null);
    setStore(null); // Limpiar la sucursal seleccionada
    closeDialog();
  };

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (formData) => {
      if (!company?.id) return;
      
      setIsSubmitting(true);
      setApiError(null);

      const commonOnSuccess = () => {
        getStores(company.id);
        handleClose();
      };
      
      const commonOnFailure = (error: any) => {
        const message = error.response?.data?.detail || 'Ocurrió un error inesperado.';
        setApiError(message);
      };

      try {
        if (isEditing) {
          await putStore(company.id, store.id, { name: formData.name }, () => {
            showSnackbar({ message: 'Sucursal actualizada con éxito', options: { variant: 'success' } });
            commonOnSuccess();
          });
        } else {
          await postStore(company.id, { name: formData.name }, () => {
            showSnackbar({ message: 'Sucursal creada con éxito', options: { variant: 'success' } });
            commonOnSuccess();
          });
        }
      } catch (error: any) {
        commonOnFailure(error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isEditing, store, company?.id, putStore, postStore, getStores, showSnackbar, handleClose]
  );

  return (
    <Dialog open={dialog === CREATE_STORE_DIALOG} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" className="font-bold">
          {isEditing ? 'Editar Sucursal' : 'Crear Nueva Sucursal'}
        </Typography>
      </DialogTitle>
      
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {apiError && <Alert severity="error">{apiError}</Alert>}
            
            <Controller name="name" control={control} render={({ field }) => (
              <TextField
                {...field}
                label="Nombre de la Sucursal"
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
                required
                autoFocus
              />
            )} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isDirty || !isValid || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default StoreDialog;